import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Switch,
  Modal,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../../../../config';
import { homeSecurityInfoService } from '../../../../services/homeSecurityInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../../services/encryption/credentialFields';
const logger = createLogger('HomeSecurityInput');

type RouteParams = {
  HomeSecurityInput: {
    homeSecurityId?: string;
    isEdit?: boolean;
  };
};

export const HomeSecurityInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<RouteParams, 'HomeSecurityInput'>>();
  const { homeSecurityId, isEdit } = route.params || {};
  const [hasOnlinePortal, setHasOnlinePortal] = useState(false);
  const [showSystemTypeModal, setShowSystemTypeModal] = useState(false);
  const [showAutopayMethodModal, setShowAutopayMethodModal] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [showMasterCode, setShowMasterCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [savedHomeSecurityId, setSavedHomeSecurityId] = useState<string | undefined>();
  
  const [formData, setFormData] = useState({
    companyName: '',
    systemType: '',
    accountNumber: '',
    masterCode: '',
    userCodes: '',
    serviceAddress: '',
    monitoringStationPhone: '',
    customerServicePhone: '',
    website: '',
    appName: '',
    monthlyCost: '',
    autopayEnabled: false,
    autopayMethod: '',
    contractEndDate: '',
    installationDate: '',
    warrantyInfo: '',
    notes: '',
    username: '',
    password: '',
  });

  const systemTypes = ['Alarm System', 'Security Cameras', 'Smart Home Security', 'Other'];
  const autopayMethods = ['Bank Account', 'Credit Card', 'Debit Card', 'Other'];

  useEffect(() => {
    if (isEdit && homeSecurityId) {
      loadHomeSecurityData();
    }
  }, [isEdit, homeSecurityId]);

  const loadHomeSecurityData = async () => {
    try {
      const homeSecurityData = await homeSecurityInfoService.getHomeSecurityInfoById(homeSecurityId!);
      if (homeSecurityData) {
        // Load vault credentials
        const credentials = await vaultService.getPortalCredentials(homeSecurityId!, 'home-security');
        
        setFormData({
          companyName: homeSecurityData.companyName || '',
          systemType: homeSecurityData.systemType || '',
          accountNumber: credentials?.accountNumber || '',
          masterCode: credentials?.masterCode || homeSecurityData.masterCode || '',
          userCodes: credentials?.userCodes || homeSecurityData.userCodes || '',
          serviceAddress: homeSecurityData.serviceAddress || '',
          monitoringStationPhone: homeSecurityData.monitoringStationPhone || '',
          customerServicePhone: homeSecurityData.customerServicePhone || '',
          website: homeSecurityData.website || '',
          appName: homeSecurityData.appName || '',
          monthlyCost: homeSecurityData.monthlyCost?.toString() || '',
          autopayEnabled: homeSecurityData.autopayEnabled || false,
          autopayMethod: '',
          contractEndDate: homeSecurityData.contractEndDate || '',
          installationDate: homeSecurityData.installationDate || '',
          warrantyInfo: homeSecurityData.warrantyInfo || '',
          notes: homeSecurityData.notes || '',
          username: credentials?.username || '',
          password: credentials?.password || '',
        });
        
        setHasOnlinePortal(!!(credentials?.username || credentials?.password));
      }
    } catch (error) {
      logger.error('Error loading home security data:', error);
      Alert.alert('Error', 'Failed to load home security information');
    }
  };

  // Helper: format phone as (xxx) xxx-xxxx while typing
  const formatPhoneNumber = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  // Format currency with commas
  const formatCurrency = (value: string) => {
    // Remove non-numeric characters except decimal
    const numericValue = value.replace(/[^0-9.]/g, '');
    // Split by decimal
    const parts = numericValue.split('.');
    // Format whole number with commas
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // Limit decimal places to 2
    if (parts[1]) {
      parts[1] = parts[1].substring(0, 2);
    }
    return parts.join('.');
  };

  // Format date as DD/MM/YYYY
  const formatDate = (value: string) => {
    // Remove non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, '');
    let formatted = '';
    
    if (numericValue.length <= 2) {
      formatted = numericValue;
    } else if (numericValue.length <= 4) {
      formatted = numericValue.slice(0, 2) + '/' + numericValue.slice(2);
    } else {
      formatted = numericValue.slice(0, 2) + '/' + numericValue.slice(2, 4) + '/' + numericValue.slice(4, 8);
    }
    
    return formatted;
  };

  const handlePhoneChange = (field: string, text: string) => {
    const formatted = formatPhoneNumber(text);
    setFormData({ ...formData, [field]: formatted });
  };

  const handleCurrencyChange = (text: string) => {
    const cleaned = text.replace(/^\$/, '');
    setFormData({ ...formData, monthlyCost: formatCurrency(cleaned) });
  };

  const handleSystemTypeSelect = (type: string) => {
    setFormData({ ...formData, systemType: type });
    setShowSystemTypeModal(false);
  };

  const handleAutopayMethodSelect = (method: string) => {
    setFormData({ ...formData, autopayMethod: method });
    setShowAutopayMethodModal(false);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      // K5: a login is saved only under a Recovery Code; ask before anything is written.
      if ((formData.username || formData.password || formData.accountNumber || formData.masterCode || formData.userCodes) && !(await ensureCredentialWriteKey())) {
        setSubmitting(false);
        return;
      }
      
      // Basic validation
      if (!formData.companyName) {
        Alert.alert('Validation Error', 'Company name is required');
        setSubmitting(false);
        return;
      }

      // Prepare payload for API
      const payload: any = {
        companyName: formData.companyName,
        systemType: formData.systemType || undefined,
        serviceAddress: formData.serviceAddress || undefined,
        monitoringStationPhone: formData.monitoringStationPhone.replace(/\D/g, '') || undefined,
        customerServicePhone: formData.customerServicePhone.replace(/\D/g, '') || undefined,
        website: formData.website || undefined,
        appName: formData.appName || undefined,
        monthlyCost: formData.monthlyCost ? parseFloat(formData.monthlyCost.replace(/[^0-9.]/g, '')) : undefined,
        autopayEnabled: formData.autopayEnabled,
        autopayMethod: formData.autopayMethod || undefined,
        contractEndDate: formData.contractEndDate || undefined,
        installationDate: formData.installationDate || undefined,
        warrantyInfo: formData.warrantyInfo || undefined,
        notes: formData.notes || undefined,
      };

      let savedHomeSecurity;

      if (isEdit && homeSecurityId) {
        // Update existing home security info
        savedHomeSecurity = await homeSecurityInfoService.updateHomeSecurityInfo(homeSecurityId, payload);
        // Handle wrapped response format { success, data, message }
        const updatedData = savedHomeSecurity?.data || savedHomeSecurity;
        savedHomeSecurity = updatedData;
        logger.debug('Updated home security info:', savedHomeSecurity);
      } else {
        // Create new home security info
        savedHomeSecurity = await homeSecurityInfoService.createHomeSecurityInfo(payload);
        // Handle wrapped response format { success, data, message }
        const createdData = savedHomeSecurity?.data || savedHomeSecurity;
        savedHomeSecurity = createdData;
        logger.debug('Created home security info:', savedHomeSecurity);
        setSavedHomeSecurityId(savedHomeSecurity.id);
      }

      // Store portal credentials if provided
      // Alarm codes are logins too (K5, D5): they ride in the same encrypted record.
      if (formData.username || formData.password || formData.accountNumber || formData.masterCode || formData.userCodes) {
        const credentialsToStore: any = {};
        if (formData.username) credentialsToStore.username = formData.username;
        if (formData.password) credentialsToStore.password = formData.password;
        if (formData.accountNumber) credentialsToStore.accountNumber = formData.accountNumber;
        if (formData.masterCode) credentialsToStore.masterCode = formData.masterCode;
        if (formData.userCodes) credentialsToStore.userCodes = formData.userCodes;

        await vaultService.storePortalCredentials(
          savedHomeSecurity.id,
          'home-security',
          credentialsToStore
        );
        logger.debug('Stored credentials in vault for home security:', savedHomeSecurity.id);
      }

      if (isEdit) {
        // For edit, navigate to lock/unlock screen
        (navigation as any).navigate('HomeSecurityLockUnlock', { homeSecurityId: homeSecurityId });
        return;
      }

      // Clear the form and reset states for Add Another
      setFormData({
        companyName: '',
        systemType: '',
        accountNumber: '',
        masterCode: '',
        userCodes: '',
        serviceAddress: '',
        monitoringStationPhone: '',
        customerServicePhone: '',
        website: '',
        appName: '',
        monthlyCost: '',
        autopayEnabled: false,
        autopayMethod: '',
        contractEndDate: '',
        installationDate: '',
        warrantyInfo: '',
        notes: '',
        username: '',
        password: '',
      });
      setHasOnlinePortal(false);
      
      // Show Add Another / Done buttons after successful save
      setShowAddAnotherDone(true);
      // Reset submitting so buttons are interactive
      setSubmitting(false);
    } catch (error: any) {
      logger.error('Error saving home security info:', error);
      Alert.alert('Error', 'Failed to save home security information. Please try again.');
      setSubmitting(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setFormData({
      companyName: '',
      systemType: '',
      accountNumber: '',
      masterCode: '',
      userCodes: '',
      serviceAddress: '',
      monitoringStationPhone: '',
      customerServicePhone: '',
      website: '',
      appName: '',
      monthlyCost: '',
      autopayEnabled: false,
      autopayMethod: '',
      contractEndDate: '',
      installationDate: '',
      warrantyInfo: '',
      notes: '',
      username: '',
      password: '',
    });
    setHasOnlinePortal(false);
    setShowAddAnotherDone(false);
    setSubmitting(false);
  };

  const handleDone = () => {
    setSubmitting(false);
    // Navigate to lock/unlock with the saved ID
    logger.debug('[Done Debug] Using ID:', savedHomeSecurityId);
    if (savedHomeSecurityId) {
      (navigation as any).navigate('HomeSecurityLockUnlock', { homeSecurityId: savedHomeSecurityId });
    } else {
      Alert.alert('Error', 'No home security ID found. Please try again.');
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>Home Security Provider Info</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Company Name *</Text>
            <TextInput
              style={styles.input}
              value={formData.companyName}
              onChangeText={(text) => setFormData({ ...formData, companyName: text })}
              placeholder="Ex: ADT Security Services"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>System Type</Text>
            <TouchableOpacity
              style={[styles.input, styles.dropdownButton]}
              onPress={() => setShowSystemTypeModal(true)}
            >
              <Text style={[styles.inputText, !formData.systemType && styles.placeholderText]}>
                {formData.systemType || 'Select system type'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Account Number 🔒</Text>
            <View style={styles.passwordContainer}>
              <TextInput
                style={[styles.input, styles.passwordInput]}
                value={formData.accountNumber}
                onChangeText={(text) => setFormData({ ...formData, accountNumber: text })}
                placeholder="Enter Account Number"
                placeholderTextColor="#A9A9A9"
                secureTextEntry={!showAccountNumber}
              />
              <TouchableOpacity
                style={styles.eyeButton}
                onPress={() => setShowAccountNumber(!showAccountNumber)}
              >
                <Image
                  source={showAccountNumber ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                  style={styles.eyeIcon}
                />
              </TouchableOpacity>
            </View>
            <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Master Code 🔒</Text>
            <View style={styles.passwordContainer}>
              <TextInput
                style={[styles.input, styles.passwordInput]}
                value={formData.masterCode}
                onChangeText={(text) => setFormData({ ...formData, masterCode: text })}
                placeholder="Enter Master Code"
                placeholderTextColor="#A9A9A9"
                secureTextEntry={!showMasterCode}
              />
              <TouchableOpacity
                style={styles.eyeButton}
                onPress={() => setShowMasterCode(!showMasterCode)}
              >
                <Image
                  source={showMasterCode ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                  style={styles.eyeIcon}
                />
              </TouchableOpacity>
            </View>
            <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>User Codes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.userCodes}
              onChangeText={(text) => setFormData({ ...formData, userCodes: text })}
              placeholder="Ex: Guest: 1234, Cleaning: 5678"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={3}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Service Address</Text>
            <TextInput
              style={styles.input}
              value={formData.serviceAddress}
              onChangeText={(text) => setFormData({ ...formData, serviceAddress: text })}
              placeholder="Ex: 123 Main St, City, State 12345"
              placeholderTextColor="#A9A9A9"
              multiline
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Monitoring Station Phone</Text>
            <TextInput
              style={styles.input}
              value={formData.monitoringStationPhone}
              onChangeText={(text) => handlePhoneChange('monitoringStationPhone', text)}
              placeholder="Ex: (555) 123-4567"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Customer Service Phone</Text>
            <TextInput
              style={styles.input}
              value={formData.customerServicePhone}
              onChangeText={(text) => handlePhoneChange('customerServicePhone', text)}
              placeholder="Ex: (555) 987-6543"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Website</Text>
            <TextInput
              style={styles.input}
              value={formData.website}
              onChangeText={(text) => setFormData({ ...formData, website: text })}
              placeholder="Ex: www.adt.com"
              placeholderTextColor="#A9A9A9"
              keyboardType="url"
              autoCapitalize="none"
            />
          </View>

          {/* Portal Toggle - Moved after website */}
          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={styles.toggleContainer}
              onPress={() => setHasOnlinePortal(!hasOnlinePortal)}
            >
              <View style={[styles.toggle, hasOnlinePortal && styles.toggleActive]}>
                <View style={[styles.toggleThumb, hasOnlinePortal && styles.toggleThumbActive]} />
              </View>
              <Text style={styles.toggleLabel}>Does your security provider have an online portal?</Text>
            </TouchableOpacity>
            <Text style={styles.helperText}>
              If yes, enter your login credentials. These will be securely stored in your vault and will only be shared with your successor after your End of Life event.
            </Text>
          </View>

          {hasOnlinePortal && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Username 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.username}
                    onChangeText={(text) => setFormData({ ...formData, username: text })}
                    placeholder="Enter Username"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showUsername}
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowUsername(!showUsername)}
                  >
                    <Image
                      source={showUsername ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Password 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.password}
                    onChangeText={(text) => setFormData({ ...formData, password: text })}
                    placeholder="Enter Password"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showPassword}
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Image
                      source={showPassword ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>
            </>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>App Name</Text>
            <TextInput
              style={styles.input}
              value={formData.appName}
              onChangeText={(text) => setFormData({ ...formData, appName: text })}
              placeholder="Ex: ADT Control App"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Monthly Cost</Text>
            <TextInput
              style={styles.input}
              value={formData.monthlyCost ? `$${formData.monthlyCost}` : ''}
              onChangeText={handleCurrencyChange}
              placeholder="Ex: $49.99"
              placeholderTextColor="#A9A9A9"
              keyboardType="decimal-pad"
            />
          </View>

          <View style={styles.toggleContainer}>
            <Text style={styles.toggleText}>Autopay Enabled</Text>
            <Switch
              trackColor={{ false: "#767577", true: "#29BFC0" }}
              thumbColor={formData.autopayEnabled ? "#fff" : "#f4f3f4"}
              ios_backgroundColor="#767577"
              onValueChange={(value) => setFormData({ ...formData, autopayEnabled: value })}
              value={formData.autopayEnabled}
            />
          </View>

          {formData.autopayEnabled && (
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Autopay Method</Text>
              <TouchableOpacity
                style={[styles.input, styles.dropdownButton]}
                onPress={() => setShowAutopayMethodModal(true)}
              >
                <Text style={[styles.inputText, !formData.autopayMethod && styles.placeholderText]}>
                  {formData.autopayMethod || 'Select autopay method'}
                </Text>
                <Image
                  source={require('../../../../assets/icons/down.png')}
                  style={styles.dropdownIcon}
                />
              </TouchableOpacity>
            </View>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Contract End Date</Text>
            <TextInput
              style={styles.input}
              value={formData.contractEndDate}
              onChangeText={(text) => setFormData({ ...formData, contractEndDate: formatDate(text) })}
              placeholder="DD/MM/YYYY"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
              maxLength={10}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Installation Date</Text>
            <TextInput
              style={styles.input}
              value={formData.installationDate}
              onChangeText={(text) => setFormData({ ...formData, installationDate: formatDate(text) })}
              placeholder="DD/MM/YYYY"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
              maxLength={10}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Warranty Information</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.warrantyInfo}
              onChangeText={(text) => setFormData({ ...formData, warrantyInfo: text })}
              placeholder="Enter warranty details"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={3}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.notes}
              onChangeText={(text) => setFormData({ ...formData, notes: text })}
              placeholder="Additional information..."
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity 
                style={styles.addAnotherButton} 
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={styles.doneButton} 
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.submitButtonContainer}>
              {submitting ? (
                <TouchableOpacity style={[styles.submitButton, styles.buttonDisabled]} disabled>
                  <ActivityIndicator color="#FFFFFF" />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={styles.submitButton}
                  onPress={handleSubmit}
                >
                  <Text style={styles.submitButtonText}>{isEdit ? 'Update' : 'Submit'}</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />

      {/* System Type Modal */}
      <Modal
        visible={showSystemTypeModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowSystemTypeModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select System Type</Text>
              <TouchableOpacity
                onPress={() => setShowSystemTypeModal(false)}
                style={styles.modalCloseButton}
              >
                <Text style={styles.modalCloseText}>×</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {systemTypes.map((type) => (
                <TouchableOpacity
                  key={type}
                  style={styles.stateItem}
                  onPress={() => handleSystemTypeSelect(type)}
                >
                  <Text style={styles.stateItemText}>{type}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Autopay Method Modal */}
      <Modal
        visible={showAutopayMethodModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowAutopayMethodModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Autopay Method</Text>
              <TouchableOpacity
                onPress={() => setShowAutopayMethodModal(false)}
                style={styles.modalCloseButton}
              >
                <Text style={styles.modalCloseText}>×</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {autopayMethods.map((method) => (
                <TouchableOpacity
                  key={method}
                  style={styles.stateItem}
                  onPress={() => handleAutopayMethodSelect(method)}
                >
                  <Text style={styles.stateItemText}>{method}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(120),
    gap: ms(20),
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
  },
  dropdownButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  inputText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  placeholderText: {
    color: '#A9A9A9',
  },
  vaultText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    fontWeight: '600',
  },
  passwordContainer: {
    position: 'relative',
  },
  passwordInput: {
    paddingRight: ms(50),
  },
  eyeButton: {
    position: 'absolute',
    right: ms(15),
    top: ms(15),
    padding: ms(5),
  },
  eyeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(10),
  },
  toggle: {
    width: s(50),
    height: s(30),
    borderRadius: ms(15),
    backgroundColor: '#E5E7EB',
    marginRight: ms(15),
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleActive: {
    backgroundColor: '#29BFC0',
  },
  toggleThumb: {
    width: s(26),
    height: s(26),
    borderRadius: ms(13),
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  toggleText: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginRight: ms(16),
  },
  helperText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    lineHeight: ms(18, 0.3),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '80%',
    width: '100%',
    paddingBottom: ms(20),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseButton: {
    padding: ms(8),
  },
  modalCloseText: {
    fontSize: ms(20, 0.3),
    color: '#1C2C4F',
  },
  stateList: {
    padding: ms(16),
  },
  stateItem: {
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  stateItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
});